import React, { useMemo, useState, useEffect, useRef, memo } from "react";
import { NodeResizer, Handle, Position } from "@xyflow/react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useStore } from "../App/store";

const MIN_SIZE = 50;

const ImageNode = ({ data, selected, id }) => {
  const focusValue = useStore((state) => state.focusValue);
  const updatePresentationOrder = useStore(
    (state) => state.updatePresentationOrder,
  );
  const presentationOrderInputRef = useRef<HTMLInputElement>(null);
  const aspectRatio = useMemo(() => {
    if (data.objectWidth && data.objectHeight)
      return data.objectWidth / data.objectHeight;
    return 1;
  }, [data.objectWidth, data.objectHeight]);
  const [pdfDialogVisible, setPdfDialogVisible] = useState(false);
  const [imageDialogVisible, setImageDialogVisible] = useState(false);
  const [rotation, setRotation] = useState(data.rotation || 0);
  const [zoom, setZoom] = useState(1);

  // Initialize rotation from data when component mounts or data changes
  useEffect(() => {
    if (data.rotation !== undefined) {
      setRotation(data.rotation);
    }
  }, [data.rotation]);

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleResetView = () => {
    setZoom(1);
    setRotation(0);
  };

  const handlePresentationOrder = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const parsedValue = parseInt(evt.target.value, 10);
    const value = isNaN(parsedValue) ? "" : parsedValue.toString();
    updatePresentationOrder(id, value);
  };

  const handleFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
    evt.target.select();
  };

  const nodeWidth = data.objectWidth || 200;
  const nodeHeight = data.objectHeight || 200;

  const onResize = (
    _evt: any,
    { width: newWidth, height: newHeight }: { width: number; height: number },
  ) => {
    let w = newWidth,
      h = newHeight;
    if (aspectRatio > 1) {
      h = Math.max(MIN_SIZE, Math.round(w / aspectRatio));
    } else {
      w = Math.max(MIN_SIZE, Math.round(h * aspectRatio));
    }
    data.objectWidth = w;
    data.objectWidth = w;
    data.objectHeight = h;
    // Optionally, trigger a re-render or update via context or props if needed
  };

  return (
    <>
      {focusValue === "presentation" && (
        <div className="presentation-order-area">
          <input
            id="presentation-order-input-field"
            className="presentation-order-input-field"
            spellCheck="false"
            value={data.presentationOrder}
            onChange={handlePresentationOrder}
            onFocus={handleFocus}
            ref={presentationOrderInputRef}
          />
        </div>
      )}
      <div
        className="dragHandle"
        style={{
          width: nodeWidth,
          height: nodeHeight,
          position: "relative",
          borderWidth: 2,
          borderStyle: "solid",
          borderColor: "#bfd2e3",
          background: "#fff",
          overflow: "hidden",
          borderRadius: 4,
          boxShadow: selected ? "0 0 8px #bfd2e3" : undefined,
          cursor: "grab",
        }}
        onDoubleClick={() => {
          if (data.label === "pdf") {
            setPdfDialogVisible(true);
          } else if (data.label === "image") {
            setImageDialogVisible(true);
          }
        }}
      >
        {/* Add handles for connections */}
        <Handle type="target" position={Position.Top} />
        <Handle type="source" position={Position.Top} />
        <Handle
          type="source"
          position={Position.Right}
          id="cross-link"
          style={{ background: "red" }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="cross-link"
          style={{ background: "blue" }}
        />
        <NodeResizer
          color="antracite"
          isVisible={selected}
          minWidth={MIN_SIZE}
          minHeight={MIN_SIZE}
          onResize={onResize}
          keepAspectRatio
        />
        {data.label === "pdf" ? (
          <iframe
            src={`${data.object}#toolbar=0&navpanes=0&scrollbar=0`}
            title="PDF Preview"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              pointerEvents: "none",
              userSelect: "none",
              borderRadius: 0,
            }}
            draggable={false}
          />
        ) : (
          <>
            <img
              src={data.object}
              alt="node"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                pointerEvents: "none",
                userSelect: "none",
                borderRadius: 0,
                transform: `rotate(${data.rotation || 0}deg)`,
              }}
              draggable={false}
              // onDoubleClick={() => setImageDialogVisible(true)}
            />
          </>
        )}
      </div>

      <Dialog
        header="Image Preview"
        visible={imageDialogVisible}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => {
          setImageDialogVisible(false);
          setZoom(1);
          // Save rotation to node data
          data.rotation = rotation;
        }}
        maximizable
        modal
        onClick={(e) => e.stopPropagation()}
        contentStyle={{
          height: "80vh",
          padding: 0,
          display: "flex",
          flexDirection: "column",
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              padding: "10px",
            }}
          >
            <Button
              icon="pi pi-search-minus"
              onClick={handleZoomOut}
              className="p-button-outlined"
              disabled={zoom <= 0.5}
              tooltip="Zoom Out"
              style={{ width: "80px" }}
            />
            <Button
              icon="pi pi-replay"
              onClick={handleResetView}
              className="p-button-outlined"
              tooltip="Reset View"
              style={{ width: "80px" }}
            />
            <Button
              icon="pi pi-search-plus"
              onClick={handleZoomIn}
              className="p-button-outlined"
              disabled={zoom >= 3}
              tooltip="Zoom In"
              style={{ width: "80px" }}
            />
            <Button
              icon="pi pi-refresh"
              onClick={handleRotate}
              className="p-button-outlined"
              tooltip="Rotate 90°"
              style={{ width: "80px" }}
            />
          </div>
        }
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: zoom > 1 ? "auto" : "hidden",
          }}
        >
          <img
            src={data.object}
            alt="Large Preview"
            style={{
              maxWidth: zoom > 1 ? "none" : "100%",
              maxHeight: zoom > 1 ? "none" : "100%",
              width: zoom > 1 ? `${zoom * 100}%` : "auto",
              objectFit: "contain",
              display: "block",
              transform: `rotate(${rotation}deg)`,
              transition: "transform 0.3s ease",
            }}
            draggable={false}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </Dialog>

      <Dialog
        header="PDF Document"
        visible={pdfDialogVisible}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => setPdfDialogVisible(false)}
        maximizable
        modal
        contentStyle={{ height: "80vh", padding: 0 }}
      >
        {data.label === "pdf" && (
          <iframe
            src={data.object}
            title="PDF Full View"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
          />
        )}
      </Dialog>
    </>
  );
};

export default memo(ImageNode);
